import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../api/apiClient";
import SearchSelect from "./SearchSelect";
import { formatMoneyPY } from "../utils/format";

/**
 * Registrar cobro (Incoming Payment) sobre facturas del front.
 *
 * Flujo: cliente -> factura abierta -> moneda del cobro + TC ajustable ->
 * medios de pago (efectivo/transferencia/cheque, multiforma) -> previsualizar
 * (dry_run, arma los payloads sin postear) -> registrar.
 *
 * v1: aplicación total de la factura (la suma de los medios debe igualar el
 * saldo, en la moneda de la factura). El cobro parcial es etapa 2.
 */
const hoyISO = () => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const esLocal = (m) => ["GS", "PYG", "G"].includes(String(m || "").trim().toUpperCase());

const RegistrarCobro = () => {
  const companyDb = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("sapUser") || "{}")?.CompanyDB;
    } catch {
      return null;
    }
  }, []);

  const [clientes, setClientes] = useState([]);
  const [cardCode, setCardCode] = useState("");

  const [facturas, setFacturas] = useState([]);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [factura, setFactura] = useState(null); // seleccionada

  const [cuentas, setCuentas] = useState([]);
  // Monedas de SAP (OCRN). El código se elige de acá y no se tipea: el Service
  // Layer rechaza con -4008 "Invalid currency" cualquier código que no exista.
  const [monedas, setMonedas] = useState([]);

  const [cobroMoneda, setCobroMoneda] = useState("");
  const [rate, setRate] = useState("");
  const [docDate, setDocDate] = useState(hoyISO());
  const [medios, setMedios] = useState([]);
  const [comentario, setComentario] = useState("");

  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // --- Carga inicial: clientes con saldo, cuentas contables y monedas ---
  useEffect(() => {
    const load = async () => {
      try {
        const [cliRes, ctaRes, monRes] = await Promise.all([
          apiClient.get("/inmobiliaria/clientes-con-saldo", { params: { CompanyDB: companyDb } }),
          apiClient.get("/inmobiliaria/cuentas-contables", { params: { CompanyDB: companyDb } }),
          apiClient.get("/inmobiliaria/monedas", { params: { CompanyDB: companyDb } }),
        ]);
        setClientes(cliRes?.data?.items || []);
        setCuentas(Array.isArray(ctaRes?.data) ? ctaRes.data : []);
        setMonedas(Array.isArray(monRes?.data) ? monRes.data : []);
      } catch (e) {
        setError(e?.response?.data?.error || e?.message || "Error cargando datos iniciales");
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFacturas = async (cc) => {
    setFacturas([]);
    setFactura(null);
    setPreview(null);
    if (!cc) return;
    setLoadingFacturas(true);
    setError("");
    try {
      const res = await apiClient.get("/inmobiliaria/facturas-abiertas", {
        params: { CompanyDB: companyDb, cardCode: cc },
      });
      setFacturas(res?.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error cargando facturas");
    } finally {
      setLoadingFacturas(false);
    }
  };

  const onSelectCliente = (cc) => {
    setCardCode(cc);
    loadFacturas(cc);
  };

  // Trae la cotización cargada del día para prefijar el TC (editable/ajustable).
  const fetchTC = async (moneda, fecha) => {
    if (!moneda || esLocal(moneda)) return "";
    try {
      const res = await apiClient.get("/tipo-cambio", { params: { fecha, moneda } });
      return res?.data?.encontrado ? String(res.data.tasa) : "";
    } catch {
      return "";
    }
  };

  const onSelectFactura = async (f) => {
    setFactura(f);
    setPreview(null);
    setMessage("");
    setError("");
    setCobroMoneda(f?.moneda || "");
    // Un medio por defecto; el importe lo completa el efecto según el objetivo.
    setMedios([{ tipo: "transferencia", cuenta: "", importe: "", referencia: "", numero: "", banco: "", vencimiento: "" }]);
    // Prefill del TC con la cotización del día para la moneda de la factura.
    const tc = await fetchTC(f?.moneda, docDate);
    setRate(tc);
  };

  // Si cambia la fecha del cobro, re-traer la cotización de esa fecha (si existe).
  useEffect(() => {
    if (!factura) return;
    let alive = true;
    (async () => {
      const tc = await fetchTC(factura.moneda, docDate);
      if (alive) setRate(tc);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docDate]);

  const multimoneda = !!(factura && cobroMoneda && cobroMoneda.toUpperCase() !== String(factura.moneda).toUpperCase());
  const facturaExtranjera = !!(factura && !esLocal(factura.moneda));
  const cobroExtranjera = !!(cobroMoneda && !esLocal(cobroMoneda));
  const requiereTC = facturaExtranjera || cobroExtranjera; // TC si alguna es moneda extranjera

  const saldo = factura ? Number(factura.saldo) : 0;

  // Total esperado de los medios, en la MONEDA DEL COBRO (lo que entregó el
  // cliente). Se convierte el saldo de la factura vía el TC.
  const montoObjetivo = useMemo(() => {
    if (!factura) return 0;
    const r = parseFloat(rate) || 0;
    const invLocal = facturaExtranjera ? saldo * r : saldo;
    const obj = cobroExtranjera ? (r ? invLocal / r : 0) : invLocal;
    return Math.round((obj + Number.EPSILON) * 100) / 100;
  }, [factura, rate, saldo, facturaExtranjera, cobroExtranjera]);

  const totalMedios = useMemo(
    () => medios.reduce((acc, m) => acc + (parseFloat(m.importe) || 0), 0),
    [medios]
  );
  const cuadra = factura && montoObjetivo > 0 ? Math.abs(totalMedios - montoObjetivo) < 0.01 : false;

  // Mantiene el importe del único medio sincronizado con el objetivo (al cargar
  // el TC, cambiar la moneda del cobro o la fecha). Si hay varios medios, no toca.
  useEffect(() => {
    if (!factura) return;
    setMedios((prev) => (prev.length === 1 ? [{ ...prev[0], importe: montoObjetivo || "" }] : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [montoObjetivo]);

  const setMedio = (i, campo, valor) =>
    setMedios((prev) => prev.map((m, idx) => (idx === i ? { ...m, [campo]: valor } : m)));
  const addMedio = () =>
    setMedios((prev) => [...prev, { tipo: "efectivo", cuenta: "", importe: "", referencia: "", numero: "", banco: "", vencimiento: "" }]);
  const removeMedio = (i) => setMedios((prev) => prev.filter((_, idx) => idx !== i));

  const buildBody = (dryRun) => ({
    CompanyDB: companyDb,
    cardCode,
    invoiceDocEntry: factura?.doc_entry,
    invoiceCurrency: factura?.moneda,
    invoiceBalance: factura?.saldo,
    cobroCurrency: cobroMoneda,
    rate: requiereTC ? rate : 1,
    docDate,
    comentario,
    means: medios
      .filter((m) => parseFloat(m.importe) > 0)
      .map((m) => ({
        tipo: m.tipo,
        cuenta: m.cuenta,
        importe: parseFloat(m.importe),
        referencia: m.referencia,
        numero: m.numero,
        banco: m.banco,
        vencimiento: m.vencimiento,
      })),
    dry_run: dryRun,
  });

  const onPreview = async () => {
    setError("");
    setMessage("");
    setPreview(null);
    setBusy(true);
    try {
      const res = await apiClient.post("/inmobiliaria/cobros", buildBody(true), {
        params: { CompanyDB: companyDb },
      });
      setPreview(res?.data || null);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error en la previsualización");
    } finally {
      setBusy(false);
    }
  };

  const onRegistrar = async () => {
    if (!window.confirm("¿Registrar el cobro en SAP? Esta acción crea el/los documentos de pago.")) return;
    setError("");
    setMessage("");
    setBusy(true);
    try {
      const res = await apiClient.post("/inmobiliaria/cobros", buildBody(false), {
        params: { CompanyDB: companyDb },
      });
      const creados = res?.data?.creados || [];
      setMessage(
        `Cobro registrado. Documentos creados: ${creados.map((c) => c.DocEntry).join(", ") || "—"}.`
      );
      setPreview(null);
      loadFacturas(cardCode); // refrescar saldos
    } catch (e) {
      const data = e?.response?.data;
      if (data?.parcial) {
        setError(
          `ATENCIÓN: quedó registrado parcialmente. ${data.error}. Documentos creados: ` +
            `${(data.creados || []).map((c) => c.DocEntry).join(", ")}. Revisar en SAP.`
        );
      } else {
        setError(data?.error || e?.message || "Error registrando el cobro");
      }
    } finally {
      setBusy(false);
    }
  };

  const fmt = (n, cur) => formatMoneyPY(n, cur);

  const clienteOpts = useMemo(
    () => clientes.map((c) => ({ value: c.card_code, label: `${c.card_name} (${c.card_code})` })),
    [clientes]
  );
  const normCur = (c) => {
    const u = String(c || "").trim().toUpperCase();
    return ["G", "GS", "PYG", "GUARANI", "GUARANIES"].includes(u) ? "GS" : u;
  };
  // Cuentas del medio de pago filtradas por la moneda del cobro (más las
  // multimoneda '##'). Evita elegir una cuenta GS para un cobro USD (SAP lo
  // rechaza). Si no viene la moneda de la cuenta, se muestra igual (fallback).
  const cuentaOpts = useMemo(() => {
    const target = normCur(cobroMoneda);
    return cuentas
      .filter((o) => {
        const cc = String(o.Currency || "").trim();
        if (!cc) return true;
        if (cc === "##") return true;
        return !target || normCur(cc) === target;
      })
      .map((o) => ({
        value: o.Code,
        label: `${o.Code} - ${o.Name}${o.Currency && o.Currency !== "##" ? ` [${o.Currency}]` : o.Currency === "##" ? " [multi]" : ""}`,
      }));
  }, [cuentas, cobroMoneda]);

  // Opciones del select de moneda del cobro. Siempre se incluye la moneda de la
  // factura, aunque OCRN no haya cargado, para que el valor por defecto sea válido.
  const monedaOpts = useMemo(() => {
    const opts = (monedas || [])
      .map((m) => ({ value: String(m?.Code || "").trim(), label: String(m?.Name || "").trim() }))
      .filter((o) => o.value);
    const invCur = String(factura?.moneda || "").trim();
    if (invCur && !opts.some((o) => o.value.toUpperCase() === invCur.toUpperCase())) {
      opts.unshift({ value: invCur, label: "" });
    }
    return opts;
  }, [monedas, factura]);

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <h3 className="card-title mb-2 text-center">Registrar cobro</h3>
        <hr className="mt-0 mb-3" />

        {error && <div className="alert alert-danger">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        {/* Cliente */}
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Cliente</label>
            <SearchSelect
              options={clienteOpts}
              value={cardCode}
              onChange={(v) => onSelectCliente(v)}
              placeholder="Buscar cliente con saldo..."
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Fecha del cobro</label>
            <input type="date" className="form-control" value={docDate} onChange={(e) => setDocDate(e.target.value)} />
          </div>
        </div>

        {/* Facturas */}
        {cardCode && (
          <>
            <h5 className="mt-4 mb-2">Facturas abiertas</h5>
            <hr className="mt-0 mb-2" />
            {loadingFacturas ? (
              <div>Cargando facturas...</div>
            ) : facturas.length === 0 ? (
              <div className="alert alert-info">El cliente no tiene facturas abiertas del front.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Factura</th>
                      <th>Folio</th>
                      <th>Vencimiento</th>
                      <th>Moneda</th>
                      <th className="text-end">Total</th>
                      <th className="text-end">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturas.map((f) => (
                      <tr
                        key={f.doc_entry}
                        className={factura?.doc_entry === f.doc_entry ? "table-primary" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => onSelectFactura(f)}
                      >
                        <td>
                          <input
                            type="radio"
                            checked={factura?.doc_entry === f.doc_entry}
                            onChange={() => onSelectFactura(f)}
                          />
                        </td>
                        <td>{f.doc_num}</td>
                        <td>{f.folio || <span className="text-muted">—</span>}</td>
                        <td>{f.vencimiento ? String(f.vencimiento).split("T")[0] : ""}</td>
                        <td>{f.moneda}</td>
                        <td className="text-end">{fmt(f.total, f.moneda)}</td>
                        <td className="text-end fw-semibold">{fmt(f.saldo, f.moneda)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Cobro */}
        {factura && (
          <>
            <h5 className="mt-4 mb-2">Datos del cobro</h5>
            <hr className="mt-0 mb-2" />
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label">Moneda del cobro</label>
                <select
                  className="form-select"
                  value={cobroMoneda}
                  onChange={(e) => {
                    setCobroMoneda(e.target.value);
                    setPreview(null);
                  }}
                >
                  {monedaOpts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label ? `${o.value} - ${o.label}` : o.value}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Igual a la factura ({factura.moneda}) = 1 documento. Distinta = 2 (vía transitoria).
                </div>
              </div>
              {requiereTC && (
                <div className="col-md-3">
                  <label className="form-label">
                    Tipo de cambio {multimoneda ? "(ajustable)" : ""}
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    className="form-control"
                    value={rate}
                    onChange={(e) => {
                      setRate(e.target.value);
                      setPreview(null);
                    }}
                    placeholder="Ej: 7500"
                  />
                </div>
              )}
              <div className="col-md-4">
                <div className={`alert py-2 mb-0 ${cuadra ? "alert-success" : "alert-warning"}`}>
                  Saldo factura: <strong>{fmt(saldo, factura.moneda)}</strong>
                  {multimoneda && (
                    <>
                      {" "}→ A cobrar: <strong>{fmt(montoObjetivo, cobroMoneda)}</strong>
                    </>
                  )}
                  <br />
                  Medios ({cobroMoneda || factura.moneda}): <strong>{fmt(totalMedios, cobroMoneda || factura.moneda)}</strong>
                </div>
              </div>
              <div className="col-12">
                <label className="form-label">Comentario</label>
                <input
                  className="form-control"
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Se guarda en el comentario del pago y en el memo del asiento (JrnlMemo)"
                />
              </div>
            </div>

            {/* Medios de pago */}
            <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
              <h6 className="mb-0">Medios de pago (en {cobroMoneda || factura.moneda})</h6>
              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={addMedio}>
                + Agregar medio
              </button>
            </div>
            {medios.map((m, i) => (
              <div className="row g-2 align-items-end mb-2" key={i}>
                <div className="col-md-2">
                  <label className="form-label small mb-0">Tipo</label>
                  <select className="form-select form-select-sm" value={m.tipo} onChange={(e) => setMedio(i, "tipo", e.target.value)}>
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                {m.tipo !== "cheque" ? (
                  <div className="col-md-3">
                    <label className="form-label small mb-0">Cuenta (donde entró)</label>
                    <SearchSelect
                      options={cuentaOpts}
                      value={m.cuenta}
                      onChange={(v) => setMedio(i, "cuenta", v)}
                      placeholder="Cuenta..."
                    />
                  </div>
                ) : (
                  <>
                    <div className="col-md-2">
                      <label className="form-label small mb-0">N° cheque</label>
                      <input className="form-control form-control-sm" value={m.numero} onChange={(e) => setMedio(i, "numero", e.target.value)} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small mb-0">Banco</label>
                      <input className="form-control form-control-sm" value={m.banco} onChange={(e) => setMedio(i, "banco", e.target.value)} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small mb-0">Vencimiento</label>
                      <input type="date" className="form-control form-control-sm" value={m.vencimiento} onChange={(e) => setMedio(i, "vencimiento", e.target.value)} />
                    </div>
                  </>
                )}
                {m.tipo === "transferencia" && (
                  <div className="col-md-2">
                    <label className="form-label small mb-0">Referencia</label>
                    <input className="form-control form-control-sm" value={m.referencia} onChange={(e) => setMedio(i, "referencia", e.target.value)} />
                  </div>
                )}
                <div className="col-md-2">
                  <label className="form-label small mb-0">Importe</label>
                  <input type="number" step="0.01" className="form-control form-control-sm" value={m.importe} onChange={(e) => setMedio(i, "importe", e.target.value)} />
                </div>
                <div className="col-md-1">
                  <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => removeMedio(i)} disabled={medios.length <= 1}>
                    ×
                  </button>
                </div>
              </div>
            ))}

            {multimoneda && (
              <div className="alert alert-info py-2 mt-2 small">
                Cobro en otra moneda: se generarán <strong>2 documentos</strong> (pago 1 a cuenta con TC {rate || "—"} + pago 2
                por la cuenta transitoria que cancela la factura).
              </div>
            )}

            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-outline-primary" type="button" onClick={onPreview} disabled={busy || !cuadra}>
                {busy ? "Procesando..." : "Previsualizar"}
              </button>
              <button className="btn btn-primary" type="button" onClick={onRegistrar} disabled={busy || !cuadra}>
                Registrar cobro
              </button>
              {!cuadra && <span className="text-danger align-self-center small">La suma de los medios debe igualar el saldo.</span>}
            </div>

            {/* Previsualización (dry_run) */}
            {preview && (
              <div className="mt-3">
                <h6>Previsualización ({preview.resumen?.cantidad_documentos} documento/s)</h6>
                {(preview.documents || []).map((d, idx) => (
                  <div className="card mb-2" key={idx}>
                    <div className="card-header py-1 small fw-semibold">{d.descripcion}</div>
                    <pre className="mb-0 p-2 small" style={{ background: "#f8f9fa", overflow: "auto" }}>
                      {JSON.stringify(d.payload, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RegistrarCobro;
